import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Trophy, User, Check, X as XIcon, CircleHelp, Sparkles } from 'lucide-react';

interface TwentyQuestionsProps {
  onGameEnd: (winner: string | null) => void;
  isHost: boolean;
  sendGameState: (state: any) => void;
  gameId: string;
  isOffline?: boolean;
}

type Answer = 'yes' | 'no' | 'maybe';

interface HistoryEntry {
  question: string;
  answer: Answer;
}

interface SharedState {
  history: HistoryEntry[];
  pendingQuestion: string | null;
  pendingGuess: string | null;
  questionsAsked: number;
  maxQuestions: number;
  gamePhase: 'playing' | 'finished';
  winner: 'host' | 'guest' | null;
  revealedSecret: string | null;
  wrongGuess: string | null;
}

const SECRETS = [
  'Pizza', 'Elephant', 'Guitar', 'Banana', 'Octopus', 'Volcano',
  'Helicopter', 'Coffee', 'Penguin', 'Tornado', 'Rainbow', 'Cactus',
  'Telescope', 'Submarine', 'Pineapple', 'Lightning', 'Camera',
  'Dolphin', 'Pyramid', 'Saxophone', 'Snowflake', 'Robot', 'Castle',
  'Dragon', 'Treasure', 'Spaghetti', 'Crocodile', 'Skateboard',
  'Microwave', 'Astronaut', 'Kangaroo', 'Trampoline', 'Avocado',
  'Lighthouse', 'Hamburger', 'Butterfly', 'Volleyball', 'Igloo',
  'Whale', 'Mountain',
];

const INITIAL_STATE: SharedState = {
  history: [],
  pendingQuestion: null,
  pendingGuess: null,
  questionsAsked: 0,
  maxQuestions: 20,
  gamePhase: 'playing',
  winner: null,
  revealedSecret: null,
  wrongGuess: null,
};

// Firebase converts arrays to objects — normalise nested arrays back
function normalizeState(data: any): SharedState {
  const toArr = (v: any) => (Array.isArray(v) ? v : Object.values(v ?? {}));
  return {
    ...data,
    history: toArr(data.history),
  };
}

export default function TwentyQuestions({
  onGameEnd,
  isHost,
  sendGameState,
  isOffline,
}: TwentyQuestionsProps) {
  const secretRef = useRef<string | null>(null);
  const [state, setState] = useState<SharedState>(INITIAL_STATE);
  const [questionInput, setQuestionInput] = useState('');
  const [guessInput, setGuessInput] = useState('');
  const [showGuess, setShowGuess] = useState(false);

  // Host picks the secret on mount
  useEffect(() => {
    if (isHost && !secretRef.current) {
      secretRef.current = SECRETS[Math.floor(Math.random() * SECRETS.length)];
    }
  }, [isHost]);

  // Listen for opponent state updates
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const payload = e.detail;
      if (payload?.type !== 'twentyquestions') return;
      const next = normalizeState(payload.data);
      setState(next);
      if (next.gamePhase === 'finished' && next.winner) {
        setTimeout(() => onGameEnd(next.winner!), 1800);
      }
    };
    window.addEventListener('game_state', handler as EventListener);
    return () => window.removeEventListener('game_state', handler as EventListener);
  }, [onGameEnd]);

  const broadcast = (next: SharedState) => {
    setState(next);
    sendGameState({ type: 'twentyquestions', data: next });
    if (next.gamePhase === 'finished' && next.winner) {
      setTimeout(() => onGameEnd(next.winner!), 1800);
    }
  };

  // Host auto-verifies any pending final guess from the guesser
  useEffect(() => {
    if (!isHost) return;
    if (state.gamePhase !== 'playing') return;
    if (!state.pendingGuess || !secretRef.current) return;
    const correct =
      state.pendingGuess.trim().toLowerCase() === secretRef.current.trim().toLowerCase();
    broadcast({
      ...state,
      pendingGuess: null,
      gamePhase: 'finished',
      winner: correct ? 'guest' : 'host',
      revealedSecret: secretRef.current,
      wrongGuess: correct ? null : state.pendingGuess,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pendingGuess, isHost]);

  const questionsRemaining = state.maxQuestions - state.questionsAsked;
  const awaitingAnswer = state.pendingQuestion !== null;
  const awaitingGuess = state.pendingGuess !== null;

  const askQuestion = () => {
    const q = questionInput.trim();
    if (!q || isHost || awaitingAnswer || awaitingGuess) return;
    if (state.gamePhase !== 'playing' || questionsRemaining <= 0) return;
    broadcast({ ...state, pendingQuestion: q });
    setQuestionInput('');
  };

  const answerQuestion = (answer: Answer) => {
    if (!isHost || !state.pendingQuestion) return;
    broadcast({
      ...state,
      pendingQuestion: null,
      history: [...state.history, { question: state.pendingQuestion, answer }],
      questionsAsked: state.questionsAsked + 1,
    });
  };

  const submitGuess = () => {
    const g = guessInput.trim();
    if (!g || isHost || awaitingAnswer || awaitingGuess) return;
    if (state.gamePhase !== 'playing') return;
    broadcast({ ...state, pendingGuess: g });
    setGuessInput('');
    setShowGuess(false);
  };

  if (isOffline) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-slate-800 rounded-xl text-center">
        <h2 className="text-2xl font-bold text-white mb-2">20 Questions</h2>
        <p className="text-gray-300">
          This game needs an opponent. Find a match to start playing.
        </p>
      </div>
    );
  }

  const myRole: 'host' | 'guest' = isHost ? 'host' : 'guest';
  const iWon = state.gamePhase === 'finished' && state.winner === myRole;
  const draw = state.gamePhase === 'finished' && state.winner === null;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-slate-800 rounded-xl">
      <div className="text-center mb-4">
        <h2 className="text-3xl font-bold text-white mb-1">20 Questions</h2>
        <p className="text-gray-300 text-sm">
          {isHost
            ? 'You picked the secret — answer yes, no, or maybe.'
            : 'Ask yes/no questions to figure out the secret.'}
        </p>
      </div>

      <div className="flex justify-between items-center mb-4 p-3 bg-slate-700 rounded-lg">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-blue-400" />
          <span className="text-white text-sm">
            {isHost ? 'Thinker' : 'Guesser'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <CircleHelp className="w-5 h-5 text-yellow-400" />
          <span className="text-white font-mono text-sm">
            {questionsRemaining} / {state.maxQuestions} left
          </span>
        </div>
      </div>

      {isHost && secretRef.current && state.gamePhase === 'playing' && (
        <div className="mb-4 p-3 bg-slate-900 rounded-lg flex items-center gap-2 border border-slate-700">
          <Sparkles className="w-4 h-4 text-violet-400 shrink-0" />
          <span className="text-sm text-gray-300">
            Your secret: <span className="text-white font-semibold">{secretRef.current}</span>
          </span>
        </div>
      )}

      <div className="mb-4 max-h-72 overflow-y-auto space-y-2 pr-1">
        {state.history.length === 0 && !awaitingAnswer && (
          <div className="text-center text-gray-400 text-sm py-6">
            No questions yet — {isHost ? 'waiting for the guesser…' : 'ask your first yes/no question.'}
          </div>
        )}
        {state.history.map((entry, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-700 rounded-lg p-3"
          >
            <div className="text-xs text-gray-400 mb-1">Q{i + 1}</div>
            <div className="text-white text-sm mb-1">{entry.question}</div>
            <div
              className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${
                entry.answer === 'yes'
                  ? 'bg-green-500/20 text-green-300'
                  : entry.answer === 'no'
                  ? 'bg-red-500/20 text-red-300'
                  : 'bg-yellow-500/20 text-yellow-300'
              }`}
            >
              {entry.answer.toUpperCase()}
            </div>
          </motion.div>
        ))}
        {awaitingAnswer && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-700 rounded-lg p-3 border border-violet-500/40"
          >
            <div className="text-xs text-violet-300 mb-1">
              Q{state.questionsAsked + 1} · awaiting answer
            </div>
            <div className="text-white text-sm">{state.pendingQuestion}</div>
          </motion.div>
        )}
        {awaitingGuess && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-700 rounded-lg p-3 border border-pink-500/40"
          >
            <div className="text-xs text-pink-300 mb-1">Final guess</div>
            <div className="text-white text-sm">"{state.pendingGuess}"</div>
          </motion.div>
        )}
      </div>

      {state.gamePhase === 'playing' && isHost && (
        <div>
          {awaitingAnswer ? (
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => answerQuestion('yes')}
                className="px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Yes
              </button>
              <button
                onClick={() => answerQuestion('no')}
                className="px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <XIcon className="w-4 h-4" />
                No
              </button>
              <button
                onClick={() => answerQuestion('maybe')}
                className="px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <CircleHelp className="w-4 h-4" />
                Maybe
              </button>
            </div>
          ) : (
            <div className="text-center text-gray-400 text-sm py-3">
              Waiting for the guesser's next question…
            </div>
          )}
        </div>
      )}

      {state.gamePhase === 'playing' && !isHost && !awaitingAnswer && !awaitingGuess && (
        <div className="space-y-2">
          {!showGuess ? (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={questionInput}
                  onChange={(e) => setQuestionInput(e.target.value.slice(0, 140))}
                  onKeyDown={(e) => e.key === 'Enter' && askQuestion()}
                  placeholder={
                    questionsRemaining > 0
                      ? 'Ask a yes/no question…'
                      : 'No questions left — make your guess!'
                  }
                  className="flex-1 px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  disabled={questionsRemaining <= 0}
                />
                <button
                  onClick={askQuestion}
                  disabled={!questionInput.trim() || questionsRemaining <= 0}
                  className="px-4 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Ask
                </button>
              </div>
              <button
                onClick={() => setShowGuess(true)}
                className="w-full px-4 py-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-200 rounded-lg text-sm font-semibold border border-violet-500/40"
              >
                Make my final guess
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={guessInput}
                  onChange={(e) => setGuessInput(e.target.value.slice(0, 60))}
                  onKeyDown={(e) => e.key === 'Enter' && submitGuess()}
                  placeholder="Type your final guess…"
                  autoFocus
                  className="flex-1 px-4 py-3 bg-slate-700 border border-pink-500/40 rounded-lg text-white"
                />
                <button
                  onClick={submitGuess}
                  disabled={!guessInput.trim()}
                  className="px-4 py-3 bg-pink-500 text-white rounded-lg font-semibold hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Guess
                </button>
              </div>
              <button
                onClick={() => {
                  setShowGuess(false);
                  setGuessInput('');
                }}
                className="w-full text-xs text-gray-400 hover:text-gray-200 py-1"
              >
                Cancel — keep asking
              </button>
              <p className="text-xs text-gray-400 text-center">
                One shot only. Wrong guess ends the game.
              </p>
            </>
          )}
        </div>
      )}

      {state.gamePhase === 'playing' && !isHost && (awaitingAnswer || awaitingGuess) && (
        <div className="text-center text-gray-400 text-sm py-3">
          Waiting for your opponent…
        </div>
      )}

      {state.gamePhase === 'finished' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-6 bg-slate-700 rounded-lg mt-2"
        >
          <Trophy className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
          <h3 className="text-2xl font-bold text-white mb-2">
            {iWon ? 'You Won!' : draw ? "It's a Draw!" : 'You Lost!'}
          </h3>
          {state.revealedSecret && (
            <p className="text-gray-300 mb-1">
              The secret was:{' '}
              <span className="text-white font-bold">{state.revealedSecret}</span>
            </p>
          )}
          {state.wrongGuess && (
            <p className="text-gray-400 text-sm">
              Guess submitted: "{state.wrongGuess}"
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
}
